// ── Geometry ──
export type Pos = { row: number; col: number };
export type Direction = "up" | "down" | "left" | "right";

export const ALL_DIRECTIONS: Direction[] = ["up", "right", "down", "left"];

// ── Emitters ──
export type EmitterShape = "single" | "L" | "line" | "cross";

export interface EmitterType {
  shape: EmitterShape;
  range: number; // 1-3 cells per arm
}

export interface PlacedEmitter {
  id: string;
  type: EmitterType;
  pos: Pos;
  rotation: number; // 0-3 (90deg increments)
  fixed: boolean; // pre-placed by puzzle (locked)
}

export interface InventoryEmitter {
  id: string;
  type: EmitterType;
  rotation: number; // current rotation
}

// ── Board cells ──
export type CellKind = "empty" | "clue" | "wall";

export interface Cell {
  kind: CellKind;
  clueValue?: number; // only for kind === "clue"
}

export type Board = Cell[][];

// ── Signal ──
export type SignalMap = number[][]; // signal count per cell

export type ClueStatus = "satisfied" | "exceeded" | "unsatisfied";

// ── Puzzle ──
export interface DifficultyTier {
  name: string;
  rows: number;
  cols: number;
  minRange: number;
  maxRange: number;
  shapes: EmitterShape[];
  walls: boolean;
  blind: boolean;
  emitterCount: number;
  fixedRatio: number;
  clueCount: number;
  parTimeMs: number;
}

export interface Puzzle {
  id: string;
  board: Board;
  fixedEmitters: PlacedEmitter[];
  inventory: InventoryEmitter[];
  difficulty: DifficultyTier;
  totalClues: number;
}

// ── Game state ──
export type GamePhase = "placing" | "won";

export interface GameState {
  puzzle: Puzzle;
  board: Board;
  placedEmitters: PlacedEmitter[]; // fixed + player-placed
  inventory: InventoryEmitter[];
  heldEmitterId: string | null;
  phase: GamePhase;
  moves: number;
  undoCount: number;
  startedAt: number;
  completedAt: number | null;
  stars: number;
  submitted: boolean; // for blind mode
}

// ── Storage ──
export interface DailyRecord {
  puzzleId: string;
  completed: boolean;
  stars: number;
  moves: number;
  timeMs: number;
}

export interface PlayerStats {
  version: number;
  streak: number;
  maxStreak: number;
  totalPlayed: number;
  totalStars: number;
  history: Record<string, DailyRecord>;
}

// ── Helpers ──
export function posKey(p: Pos): string {
  return `${p.row},${p.col}`;
}

export function posEq(a: Pos, b: Pos): boolean {
  return a.row === b.row && a.col === b.col;
}
